import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Input, colors, shadowMaps, spacing } from '@sine/sine-ui-components';

import useDebounce from '../../../common/hooks/useDebounce';

const Container = styled.div`
  position: relative;
`;

const DropdownMenuStyles = css`
  position: absolute;
  padding: 5px 0;
  margin: ${spacing(1)} 0;
  list-style: none;
  font-size: 14px;
  text-align: left;
  background-color: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: ${spacing(0.5)};
  box-shadow: ${shadowMaps.medium};
  max-height: 300px;
  overflow-y: auto;
  top: 74%;

  min-width: 200px;
  white-space: nowrap;
  z-index: 98;
`;

const DropdownMenu = styled.ul`
  ${DropdownMenuStyles}

  & > li > a {
    display: block;
    padding: ${spacing(1)} ${spacing(1.5)};
    color: ${colors.black};
    white-space: nowrap;
  }

  & > li > a:hover {
    text-decoration: none;
    color: ${colors.black};
    background-color: ${colors.grey200};
  }
`;

const EmptyDropdownMenu = styled.div`
  ${DropdownMenuStyles}

  padding: ${spacing(1)} ${spacing(1.5)};
  color: ${colors.black};
`;

function AutoCompleteInput({
  id,
  name,
  label,
  placeholder,
  options = [],
  defaultValue = '',
  onSelect,
  onDebounceChange,
  onClear,
  emptyMessage,
  disabled,
}) {
  const [value, setValue] = useState(defaultValue);
  const [selected, setSelected] = useState(false);
  const [isShowingDropdown, setIsShowingDropdown] = useState(false);
  const debouncedValue = useDebounce(value, 500);

  const hideDropdown = () => {
    setIsShowingDropdown(false);
    onClear();
    window.removeEventListener('resize', hideDropdown);
  };

  const onOptionSelect = (option) => {
    setSelected(true);
    setValue(option.value);
    onSelect(option);
    hideDropdown();
  };

  const showDropdown = () => {
    setIsShowingDropdown(true);
    window.addEventListener('resize', hideDropdown);
  };

  useEffect(() => {
    if (!value || selected || value === defaultValue) {
      hideDropdown();
    } else if (value.length >= 2) {
      onDebounceChange(debouncedValue);
      if (!isShowingDropdown) {
        showDropdown();
      }
    }
    setSelected(false);
  }, [debouncedValue]);

  useEffect(() => {
    if (options.length) {
      showDropdown();
    }
  }, [options]);

  return (
    <Container data-st-id='autocomplete'>
      <Input
        type='text'
        id={id}
        name={name}
        autoComplete='new-password'
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={setValue}
        onBlur={hideDropdown}
        disabled={disabled}
        data-st-id='autocomplete-input'
      />
      {isShowingDropdown &&
        (emptyMessage ? (
          <EmptyDropdownMenu data-st-id='autocomplete-dropdown-empty'>{emptyMessage}</EmptyDropdownMenu>
        ) : (
          !!options.length && (
            <DropdownMenu data-st-id='autocomplete-dropdown-options'>
              {options.map((option) => (
                <li key={option.id} data-st-id='autocomplete-dropdown-option'>
                  <a onMouseDown={() => onOptionSelect(option)}>{option.value}</a>
                </li>
              ))}
            </DropdownMenu>
          )
        ))}
    </Container>
  );
}

AutoCompleteInput.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  defaultValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onDebounceChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string,
  disabled: PropTypes.bool,
};

export default AutoCompleteInput;
